import { Link, useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import { useTopBurgers } from '@/data/queries/useTopBurgers';
import { transformedImageUrl, onTransformedImageError } from '@/lib/imageTransform';


interface LandingTopBurgersProps {
  /** When set, scope the ranking to a location (falls back to global). */
  locationId?: string | null;
  minCheckins?: number;
}

export default function LandingTopBurgers({
  locationId = null,
  minCheckins = 2,
}: LandingTopBurgersProps = {}) {
  const navigate = useNavigate();
  const { data: burgers = [], isFetched } = useTopBurgers({
    locationId,
    minCheckins,
    limit: 5,
    fallbackToGlobal: true,
  });

  if (!isFetched || burgers.length === 0) return null;


  return (
    <section className="w-full">


      <div className="grid gap-1.5">
        {burgers.map((burger, i) => (
          <Link
            key={burger.burger_id}
            to={`/burger/${burger.slug}`}
            className="flex items-center gap-3 p-2.5 rounded-lg bg-background/60 border border-border/60 hover:bg-primary/5 hover:border-primary/30 transition-colors group"
          >
            <span className="text-sm font-bold text-primary tabular-nums w-5 text-center flex-shrink-0">
              {i + 1}
            </span>
            {burger.image_url && (
              <img
                src={transformedImageUrl(burger.image_url, { width: 40, height: 40, quality: 70 })}
                onError={onTransformedImageError(burger.image_url)}
                alt={burger.name}
                width={40}
                height={40}
                className="w-10 h-10 rounded object-cover flex-shrink-0"
                loading="lazy"
                decoding="async"
              />
            )}

            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium group-hover:text-primary-hover transition-colors block truncate">
                {burger.name}
              </span>
              {burger.producer_name && burger.producer_slug && (
                <span
                  role="link"
                  tabIndex={0}
                  className="text-xs text-muted-foreground hover:text-primary-hover transition-colors block truncate cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/producer/${burger.producer_slug}`);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/producer/${burger.producer_slug}`);
                    }
                  }}
                >
                  {burger.producer_name}
                </span>
              )}
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <Star className="h-3 w-3 fill-primary text-primary" />
              <span className="text-sm font-semibold tabular-nums">
                {Number(burger.avg_rating).toFixed(1)}
              </span>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between text-xs">
        <Link
          to="/leaderboard"
          className="font-semibold text-primary hover:text-primary-hover transition-colors"
        >
          See the full leaderboard →
        </Link>
        <Link
          to="/leaderboards"
          className="text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
        >
          By location
        </Link>
      </div>
    </section>
  );
}
